// Pin a conversation, give it a private name (owner, 2026-10-21).
//
// « Each user can pin conversations and add names to them. Pinning will not
// affect other users' interface — they won't see it. »
//
//   PrefsRow     a list row with its ⋯ menu (desktop: ⋯ or right-click;
//                phone: ⋯ or a long press) and the inline rename field
//   PrefsTitle   the chat header's title: the private name over the WhatsApp
//                name / number, with the same ⋯ menu
//   PrefsName    a name with the private name in front (Inbox rows)
//
// Everything here is the signed-in user's own (chat/useChatPrefs.ts). Nothing
// renders when the server has no pins yet (migration 260 not applied).
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Check, MoreHorizontal, Pencil, Pin, PinOff, X } from 'lucide-react';
import { useT } from '@/lib/i18n';
import { MAX_CUSTOM_NAME } from './chatPrefs';
import type { ChatPrefsHandle } from './useChatPrefs';

const LONG_PRESS_MS = 500;

/** The small pin in front of a pinned conversation's name. */
export function PinMark({ className = '' }: { className?: string }) {
  const { t } = useT();
  return (
    <span className={`inline-flex shrink-0 text-brand-600 ${className}`} data-pin-mark title={t('Épinglée')}>
      <Pin className="h-3.5 w-3.5 rotate-45" aria-hidden />
      <span className="sr-only">{t('Épinglée')}</span>
    </span>
  );
}

/** Type the name, Enter saves, Échap cancels; empty = the name is removed. */
export function RenameField({ initial, onSave, onCancel }: {
  initial: string; onSave: (name: string) => void; onCancel: () => void;
}) {
  const { t } = useT();
  const [value, setValue] = useState(initial);
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  return (
    <div className="flex items-center gap-1 w-full" data-rename-field onClick={(e) => e.stopPropagation()}>
      <label className="sr-only" htmlFor="chat-custom-name">{t('Nom privé (visible par vous seul)')}</label>
      <input
        ref={ref} id="chat-custom-name" type="text" value={value} maxLength={MAX_CUSTOM_NAME}
        autoComplete="off" spellCheck={false} enterKeyHint="done"
        placeholder={t('Nom privé (visible par vous seul)')}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); onSave(value); }
          else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onCancel(); }
        }}
        className="min-w-0 flex-1 h-11 rounded-lg border border-brand-300 bg-white px-3 text-[16px] md:text-[13px] text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
      />
      <button type="button" onClick={() => onSave(value)} aria-label={t('Enregistrer le nom')}
        className="h-11 w-11 shrink-0 inline-flex items-center justify-center rounded-lg bg-brand-600 text-white hover:bg-brand-700 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
        <Check className="h-4 w-4" aria-hidden />
      </button>
      <button type="button" onClick={onCancel} aria-label={t('Annuler')}
        className="h-11 w-11 shrink-0 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}

/** The ⋯ button and its two choices. Controlled, so a long press can open it. */
function PrefsMenu({ pinned, open, setOpen, onTogglePin, onRename, small = false, label }: {
  pinned: boolean; open: boolean; setOpen: (v: boolean) => void;
  onTogglePin: () => void; onRename: () => void; small?: boolean; label: string;
}) {
  const { t } = useT();
  const box = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc, true);
    return () => { document.removeEventListener('pointerdown', away); document.removeEventListener('keydown', esc, true); };
  }, [open, setOpen]);
  const item = 'w-full min-h-[44px] px-3 flex items-center gap-2.5 text-left text-[14px] md:text-[13px] text-gray-800 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none cursor-pointer';
  return (
    <div ref={box} className="relative shrink-0">
      <button type="button" aria-haspopup="menu" aria-expanded={open} aria-label={label} title={label} data-chat-prefs-menu
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className={`${small ? 'h-9 w-9' : 'h-11 w-11'} inline-flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-800 hover:bg-black/5 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500`}>
        <MoreHorizontal className="h-5 w-5" aria-hidden />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-full mt-1 z-30 w-52 py-1 rounded-xl border border-gray-200 bg-white shadow-lg storiz-fade-in"
          onClick={(e) => e.stopPropagation()}>
          <button type="button" role="menuitem" className={item} onClick={() => { setOpen(false); onTogglePin(); }}>
            {pinned ? <PinOff className="h-4 w-4 text-gray-500" aria-hidden /> : <Pin className="h-4 w-4 text-gray-500" aria-hidden />}
            {pinned ? t('Désépingler') : t('Épingler')}
          </button>
          <button type="button" role="menuitem" className={item} onClick={() => { setOpen(false); onRename(); }}>
            <Pencil className="h-4 w-4 text-gray-500" aria-hidden />
            {t('Renommer…')}
          </button>
          <p className="px-3 pt-1 pb-1.5 text-[11px] text-gray-400 leading-snug">{t('Visible par vous seul')}</p>
        </div>
      )}
    </div>
  );
}

/** Long press (touch) → open the menu, and swallow the tap that follows. */
function useLongPress(onLong: () => void) {
  const timer = useRef<number | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);
  const clear = () => { if (timer.current != null) window.clearTimeout(timer.current); timer.current = null; start.current = null; };
  useEffect(() => clear, []);
  return {
    onPointerDown: (e: React.PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      fired.current = false;
      start.current = { x: e.clientX, y: e.clientY };
      timer.current = window.setTimeout(() => { fired.current = true; timer.current = null; onLong(); }, LONG_PRESS_MS);
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (start.current && Math.hypot(e.clientX - start.current.x, e.clientY - start.current.y) > 10) clear();
    },
    onPointerUp: clear,
    onPointerCancel: clear,
    onClickCapture: (e: React.MouseEvent) => {
      if (!fired.current) return;
      fired.current = false;
      e.preventDefault();
      e.stopPropagation();
    },
  };
}

/**
 * One list row with its ⋯ menu. `children` is the row itself; give it room on
 * the right (`pr-12`) for the button when `prefs.ready`.
 */
export function PrefsRow({ prefs, chatId, phone, children }: {
  prefs: ChatPrefsHandle; chatId: string; phone?: string | null; children: ReactNode;
}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const press = useLongPress(() => setOpen(true));
  if (!prefs.ready) return <>{children}</>;
  const pref = prefs.prefOf(chatId, phone);
  return (
    <div className="relative [-webkit-touch-callout:none]" data-chat-row={chatId}
      onContextMenu={(e) => { e.preventDefault(); setOpen(true); }} {...press}>
      {renaming && (
        <div className="mb-1">
          <RenameField initial={pref?.custom_name ?? ''}
            onSave={(name) => { prefs.save(chatId, phone, { custom_name: name }); setRenaming(false); }}
            onCancel={() => setRenaming(false)} />
        </div>
      )}
      {children}
      {!renaming && <div className="absolute right-0.5 top-0.5">
        <PrefsMenu pinned={Boolean(pref?.pinned_at)} open={open} setOpen={setOpen} label={t('Options de la conversation')}
          onTogglePin={() => prefs.save(chatId, phone, { pinned: !pref?.pinned_at })}
          onRename={() => setRenaming(true)} />
      </div>}
    </div>
  );
}

/**
 * The header's title: the private name first, the WhatsApp name and number
 * under it; without one, exactly the old title. `extra` follows the second line.
 */
export function PrefsTitle({ prefs, chatId, phone, name, number, extra, variant }: {
  prefs: ChatPrefsHandle; chatId: string; phone?: string | null; name: string | null; number: string;
  extra?: ReactNode; variant: 'desktop' | 'phone';
}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  useEffect(() => { setRenaming(false); setOpen(false); }, [chatId]);
  const pref = prefs.ready ? prefs.prefOf(chatId, phone) : null;
  const custom = pref?.custom_name ?? null;
  const title = custom || name || number;
  const sub = custom ? (name ? `${name} · ${number}` : number) : (name ? number : '');
  const desk = variant === 'desktop';
  if (renaming) {
    return (
      <div className="min-w-0 flex-1 py-1">
        <RenameField initial={custom ?? ''}
          onSave={(v) => { prefs.save(chatId, phone, { custom_name: v }); setRenaming(false); }}
          onCancel={() => setRenaming(false)} />
      </div>
    );
  }
  return (
    <>
      <div className={`min-w-0 flex-1 ${desk ? '' : 'py-1.5'}`} data-chat-title>
        <p className={`flex items-center gap-1.5 min-w-0 ${desk ? 'text-[14px] font-semibold text-text-primary' : 'text-[16px] font-medium leading-tight text-[var(--wa-text)]'}`}>
          {pref?.pinned_at && <PinMark />}
          <span className="truncate">{title}</span>
        </p>
        {(sub || extra) && (
          <p className={desk ? 'text-[11.5px] text-text-tertiary tabular-nums truncate' : 'text-[12px] leading-tight text-[var(--wa-meta)] tabular-nums truncate'}>
            {sub}{sub && extra ? ' · ' : ''}{extra}
          </p>
        )}
      </div>
      {prefs.ready && (
        <PrefsMenu pinned={Boolean(pref?.pinned_at)} open={open} setOpen={setOpen} small={desk}
          label={t('Options de la conversation')}
          onTogglePin={() => prefs.save(chatId, phone, { pinned: !pref?.pinned_at })}
          onRename={() => setRenaming(true)} />
      )}
    </>
  );
}

/** A name with the private one in front — the Inbox rows (no reordering there). */
export function PrefsName({ prefs, chatId, name, className }: {
  prefs: ChatPrefsHandle; chatId: string | null; name: string; className: string;
}) {
  const pref = prefs.ready && chatId ? prefs.prefOf(chatId) : null;
  if (!pref) return <b className={className}>{name}</b>;
  return (
    <>
      {pref.pinned_at && <PinMark className="self-center" />}
      <b className={className}>{pref.custom_name || name}</b>
      {pref.custom_name && <span className="text-[11.5px] text-text-tertiary truncate min-w-0">{name}</span>}
    </>
  );
}

/** The one line that says a pin or a name did not save. */
export function PrefsError({ prefs }: { prefs: ChatPrefsHandle }) {
  const { t } = useT();
  if (!prefs.error) return null;
  return (
    <p role="alert" className="flex items-center justify-between gap-2 text-[11.5px] text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-1.5">
      <span>{t('Épingle ou nom non enregistré — réessayez.')}</span>
      <button type="button" onClick={prefs.clearError} aria-label={t('Fermer')} className="h-8 w-8 inline-flex items-center justify-center cursor-pointer">
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
    </p>
  );
}
